import React, { useEffect, useRef, useState } from 'react'
import type {
    AgenteMonedaView,
    CreateProspectoKanbanCardRequest,
    CreateProspectoKanbanNoteRequest,
    ProspectoDTO,
    ProspectoKanbanCardDTO,
    ProspectoKanbanCardStatus,
    ProspectoKanbanNotaDTO,
    RamoDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { clip } from '../../../utils/inputs'
import { searchProspectos, useLookupField } from '../../../utils/lookups'
import { parseAmount, sanitizeDecimal } from '../../../utils/numbers'
import {
    cardRequest,
    DESCRIPCION_MAX,
    errorMessage,
    formatActivityDate,
    NOMBRE_MAX,
    NOTA_MAX,
    opportunityStatusView,
} from '../common/opportunity-common'

export interface OpportunityDrawerProps {
    open: boolean
    cardId: number | null
    columnId: number | null
    columns: Array<{ id: number; nombre: string }>
    onClose: () => void
    onSaved: (card: ProspectoKanbanCardDTO) => void
    onDelete?: (cardId: number) => void
}

export interface OpportunityForm {
    nombre: string
    prospecto: ProspectoDTO | null
    ramoId: number | ''
    moneda: string
    primaTotal: string
    descripcion: string
    columnId: number | ''
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

const LOAD_ERROR = 'No se pudo cargar la oportunidad.'
const CATALOGS_ERROR = 'No se pudieron cargar los ramos y monedas.'
const SAVE_ERROR = 'No se pudo guardar la oportunidad.'
const NOTE_ERROR = 'No se pudo guardar la actividad.'

const emptyForm = (columnId: number | null, moneda = ''): OpportunityForm => ({
    nombre: '',
    prospecto: null,
    ramoId: '',
    moneda,
    primaTotal: '',
    descripcion: '',
    columnId: columnId ?? '',
})

const toForm = (card: ProspectoKanbanCardDTO): OpportunityForm => ({
    nombre: card.nombre || '',
    prospecto: card.prospecto,
    ramoId: card.ramo?.id ?? '',
    moneda: card.moneda?.abreviacion || '',
    primaTotal: card.primaTotal != null ? String(card.primaTotal) : '',
    descripcion: card.descripcion || '',
    columnId: card.columnId ?? '',
})

const sortNotes = (notes: ProspectoKanbanNotaDTO[]) =>
    [...notes].sort((left, right) => String(right.fecCreacion || '').localeCompare(String(left.fecCreacion || '')))

export const useOpportunityDrawerModel = ({ open, cardId, columnId, onSaved }: OpportunityDrawerProps) => {
    const createdId = useRef<number | null>(null)
    const [card, setCard] = useState<ProspectoKanbanCardDTO | null>(null)
    const [form, setForm] = useState<OpportunityForm>(() => emptyForm(columnId))
    const [ramos, setRamos] = useState<RamoDTO[]>([])
    const [monedas, setMonedas] = useState<AgenteMonedaView[]>([])
    const [notes, setNotes] = useState<ProspectoKanbanNotaDTO[]>([])
    const [newNote, setNewNote] = useState('')
    const [editingNote, setEditingNote] = useState<{ id: number; texto: string } | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [prospectDialog, setProspectDialog] = useState(false)

    const patch = (partial: Partial<OpportunityForm>) => setForm((current) => ({ ...current, ...partial }))

    useEffect(() => {
        if (!open || (ramos.length && monedas.length)) return undefined
        let active = true
        const load = async () => {
            try {
                const [ramosPayload, monedasPayload]: [WrapperResponse<RamoDTO[]> | null, WrapperResponse<AgenteMonedaView[]> | null] = await Promise.all([
                    APIInvoke.invokeGET('/ramos').then((response: Response) => response.json()),
                    APIInvoke.invokeGET('/monedas').then((response: Response) => response.json()),
                ])
                if (!active) return
                if (!ramosPayload?.ok || !monedasPayload?.ok) throw new Error(CATALOGS_ERROR)
                const catalogMonedas = monedasPayload.body || []
                setRamos((ramosPayload.body || []).filter((item) => item.id != null))
                setMonedas(catalogMonedas)
                if (catalogMonedas.some((item) => item.abreviacion === 'MXN')) {
                    setForm((current) => (current.moneda ? current : { ...current, moneda: 'MXN' }))
                }
            } catch (loadError) {
                if (active) setMessage({ type: 'error', message: errorMessage(loadError, CATALOGS_ERROR) })
            }
        }
        load()
        return () => { active = false }
    }, [open, ramos.length, monedas.length])

    useEffect(() => {
        if (!open) return undefined
        if (cardId && cardId === createdId.current) {
            createdId.current = null
            return undefined
        }
        setMessage(null)
        setNewNote('')
        setEditingNote(null)
        setNotes([])
        if (!cardId) {
            setCard(null)
            setForm(emptyForm(columnId, monedas.some((item) => item.abreviacion === 'MXN') ? 'MXN' : ''))
            return undefined
        }
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const [cardPayload, notesPayload]: [WrapperResponse<ProspectoKanbanCardDTO> | null, WrapperResponse<ProspectoKanbanNotaDTO[]> | null] = await Promise.all([
                    APIInvoke.invokeGET(`/prospectos/kanbans/cards/${cardId}`).then((response: Response) => response.json()),
                    APIInvoke.invokeGET(`/prospectos/kanbans/cards/${cardId}/notes`).then((response: Response) => response.json()),
                ])
                if (!active) return
                if (!cardPayload?.ok || !cardPayload.body) throw new Error(cardPayload?.message || LOAD_ERROR)
                setCard(cardPayload.body)
                setForm(toForm(cardPayload.body))
                setNotes(sortNotes(notesPayload?.ok ? notesPayload.body || [] : []))
            } catch (loadError) {
                if (active) setMessage({ type: 'error', message: errorMessage(loadError, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, cardId, columnId])

    const prospectoLookup = useLookupField<ProspectoDTO>({ value: form.prospecto, onChange: (prospecto) => patch({ prospecto }), search: searchProspectos })

    const change = {
        nombre: (value: string) => patch({ nombre: clip(value, NOMBRE_MAX) }),
        prospecto: (prospecto: ProspectoDTO | null) => patch({ prospecto }),
        ramo: (value: number | '') => patch({ ramoId: value }),
        moneda: (value: string) => patch({ moneda: value }),
        primaTotal: (value: string) => patch({ primaTotal: sanitizeDecimal(value) }),
        descripcion: (value: string) => patch({ descripcion: clip(value, DESCRIPCION_MAX) }),
        column: (value: number | '') => patch({ columnId: value }),
    }

    const validate = () => {
        if (!form.nombre.trim()) return 'Captura el nombre de la oportunidad.'
        if (!form.prospecto?.id) return 'Selecciona el prospecto.'
        if (!form.ramoId) return 'Elige el ramo.'
        if (!form.moneda) return 'Elige la moneda.'
        if (form.primaTotal === '') return 'Captura la prima total estimada.'
        if (!form.descripcion.trim()) return 'Describe la oportunidad.'
        if (!form.columnId) return 'Elige la etapa del embudo.'
        return null
    }

    const persist = async (request: CreateProspectoKanbanCardRequest) => {
        const payload: WrapperResponse<ProspectoKanbanCardDTO> = card?.id
            ? await (await APIInvoke.invokePUT(`/prospectos/kanbans/cards/${card.id}`, request)).json()
            : await (await APIInvoke.invokePOST('/prospectos/kanbans/cards', request)).json()
        if (!payload.ok || !payload.body) throw new Error(payload.message || SAVE_ERROR)
        return payload.body
    }

    const applySaved = (saved: ProspectoKanbanCardDTO, success: string) => {
        if (!card?.id) createdId.current = saved.id
        setCard(saved)
        setForm(toForm(saved))
        setMessage({ type: 'success', message: success })
        onSaved(saved)
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const invalid = validate()
        if (invalid || !form.prospecto || !form.ramoId || !form.columnId) {
            setMessage({ type: 'error', message: invalid || SAVE_ERROR })
            return
        }
        try {
            setSaving(true)
            setMessage(null)
            const movedColumn = Boolean(card && card.columnId !== form.columnId)
            const saved = await persist({
                id: card?.id ?? null,
                columnId: form.columnId,
                nombre: form.nombre.trim(),
                prospectoId: form.prospecto.id,
                descripcion: form.descripcion.trim(),
                primaTotal: parseAmount(form.primaTotal),
                ramoId: form.ramoId,
                moneda: form.moneda,
                status: card?.status || 'abierta',
                orden: movedColumn ? 0 : null,
            })
            applySaved(saved, card?.id ? 'Oportunidad actualizada' : 'Oportunidad creada')
        } catch (saveError) {
            setMessage({ type: 'error', message: errorMessage(saveError, SAVE_ERROR) })
        } finally {
            setSaving(false)
        }
    }

    const changeStatus = async (status: ProspectoKanbanCardStatus) => {
        if (!card) return
        try {
            setSaving(true)
            setMessage(null)
            const saved = await persist(cardRequest(card, { status }))
            const label = status === 'abierta' ? 'Oportunidad reabierta' : `Oportunidad cerrada como ${opportunityStatusView(status).label.toLowerCase()}`
            applySaved(saved, label)
        } catch (saveError) {
            setMessage({ type: 'error', message: errorMessage(saveError, SAVE_ERROR) })
        } finally {
            setSaving(false)
        }
    }

    const saveNote = async (request: CreateProspectoKanbanNoteRequest, noteId?: number) => {
        const payload: WrapperResponse<ProspectoKanbanNotaDTO> = noteId
            ? await (await APIInvoke.invokePUT(`/prospectos/kanbans/cards/notes/${noteId}`, request)).json()
            : await (await APIInvoke.invokePOST('/prospectos/kanbans/cards/notes', request)).json()
        if (!payload.ok || !payload.body) throw new Error(payload.message || NOTE_ERROR)
        return payload.body
    }

    const addNote = async () => {
        const texto = newNote.trim()
        if (!texto || !card) return
        try {
            setSaving(true)
            const created = await saveNote({ texto, cardId: card.id })
            setNotes((current) => [created, ...current])
            setNewNote('')
        } catch (noteError) {
            setMessage({ type: 'error', message: errorMessage(noteError, NOTE_ERROR) })
        } finally {
            setSaving(false)
        }
    }

    const updateNote = async () => {
        if (!editingNote || !card || !editingNote.texto.trim()) return
        try {
            setSaving(true)
            const updated = await saveNote({ texto: editingNote.texto.trim(), cardId: card.id }, editingNote.id)
            setNotes((current) => current.map((note) => (note.id === updated.id ? { ...note, ...updated } : note)))
            setEditingNote(null)
        } catch (noteError) {
            setMessage({ type: 'error', message: errorMessage(noteError, NOTE_ERROR) })
        } finally {
            setSaving(false)
        }
    }

    const status = card ? opportunityStatusView(card.status) : null

    return {
        card,
        isNew: !card?.id,
        form,
        ramos,
        monedas,
        loading,
        saving,
        message,
        status,
        isOpen: status?.id === 'abierta',
        title: card?.nombre || (cardId ? 'Oportunidad' : 'Nueva oportunidad'),
        prospectoLookup,
        notes: notes.map((note) => ({ id: note.id, texto: note.texto || '', fecha: formatActivityDate(note.fecCreacion) || '' })),
        newNote,
        editingNote,
        prospectDialog,
        change,
        save,
        changeStatus,
        setNewNote: (value: string) => setNewNote(clip(value, NOTA_MAX)),
        addNote,
        startEditNote: (id: number, texto: string) => setEditingNote({ id, texto }),
        changeEditingNote: (value: string) => setEditingNote((current) => (current ? { ...current, texto: clip(value, NOTA_MAX) } : current)),
        cancelEditNote: () => setEditingNote(null),
        updateNote,
        openProspectDialog: () => setProspectDialog(true),
        closeProspectDialog: () => setProspectDialog(false),
        prospectCreated: (prospecto?: ProspectoDTO | null) => {
            setProspectDialog(false)
            if (prospecto) patch({ prospecto })
        },
    }
}
